import { consume } from '@lit/context';
import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { toolbarChip } from '../../../theme/toolbar.css';

/**
 * Scoreboard Button - Opens the full scoreboard (mobile HUD only).
 *
 * A standalone toolbar chip in the top bar. Desktop reaches the
 * scoreboard via the room-header VIEW ALL button and the TAB keybind —
 * neither exists on a phone, so touch gets this dedicated control. It owns no
 * top-bar popover of its own; it just closes any open one (zoom/loadout/
 * selector) before showing the scoreboard modal.
 *
 * @element scoreboard-button
 */
@customElement('scoreboard-button')
export class ScoreboardButton extends LitElement {
  static override styles = [
    tokens,
    toolbarChip,
    css`
      :host {
        display: block;
        pointer-events: auto;
      }
    `,
  ];

  /** Game API from context */
  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  // stopPropagation keeps the click off the document/game-canvas handlers.
  // Float cleanup (top-bar popovers, context menu) is centralized in
  // closeAllPanels(), which showScorePanel() runs — so no manual close here.
  private handleClick = (e: Event) => {
    e.stopPropagation();
    this.gameApi.playClick();
    this.gameApi.showScorePanel();
  };

  override render() {
    return html`
      <button class="toolbar-chip" @click=${this.handleClick} aria-haspopup="dialog">
        <img src="/vanilla/ui/menu/podium.svg" alt="Scoreboard" />
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'scoreboard-button': ScoreboardButton;
  }
}
